import { Injectable, signal, WritableSignal, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { Observable, timer, Subscription, of, interval } from 'rxjs';
import { catchError, switchMap, tap, map, filter } from 'rxjs/operators';
import { ApiResponse, AnalysisQueueState } from '@app/types';

// Asegúrate de agregar last_update a tu interfaz en @app/types si no está
// export interface AnalysisQueueState { ... last_update?: string; }

const INITIAL_STATE: AnalysisQueueState = {
    is_processing: false,
    queue_length: 0,
    current_job: null,
    history: [],
    last_update: '' // Inicializar
};

@Injectable({ providedIn: 'root' })
export class AnalysisService {
    private readonly http = inject(HttpClient);
    private readonly API_URL = environment.API_URL;

    private _state: WritableSignal<AnalysisQueueState> = signal(INITIAL_STATE);
    public readonly state = this._state.asReadonly();

    // Cronómetro local
    private _elapsedTime: WritableSignal<number> = signal(0);
    public readonly elapsedTime = this._elapsedTime.asReadonly();

    private pollingSubscription?: Subscription;
    private timerSubscription?: Subscription;

    // Almacena el último timestamp recibido del backend
    private lastKnownTimestamp: string = '';

    constructor() { }

    startFullAnalysis(processor: string = 'QWEN'): Observable<any> {
        return this.http.post<ApiResponse<any>>(`${this.API_URL}/external-documents/analyze-all`, { processor }).pipe(
            tap(() => {
                this.startPolling();
            })
        );
    }

    pauseAnalysis(): Observable<any> {
        return this.http.post(`${this.API_URL}/analysis/pause`, {});
    }

    purgeQueue(): Observable<any> {
        return this.http.post(`${this.API_URL}/analysis/purge`, {});
    }

    getStatus(): Observable<AnalysisQueueState> {
        return this.http.get<ApiResponse<AnalysisQueueState>>(`${this.API_URL}/analysis/status`).pipe(
            map(response => {
                return Array.isArray(response.data) ? response.data[0] : response.data;
            }),
            catchError(err => {
                console.error('Error polling status', err);
                return of(this._state());
            })
        );
    }

    startPolling(): void {
        if (this.pollingSubscription) return;
        this.pollingSubscription = this.startPollingRecursive().subscribe();
    }

    private startPollingRecursive(): Observable<any> {
        return this.getStatus().pipe(
            tap((newState) => {
                // LÓGICA DE DEDUPLICACIÓN (Smart Polling)
                // Si el backend nos manda el mismo timestamp, ignoramos la actualización de la señal
                // Esto evita que Angular re-calcule computed signals y re-renderice la UI innecesariamente
                if (newState.last_update && newState.last_update === this.lastKnownTimestamp) {
                    // Solo actualizamos el timer local si hay un job corriendo
                    this.handleTimer(this._state());
                    return;
                }

                // Si hay cambio o es la primera vez
                this.lastKnownTimestamp = newState.last_update || '';
                this._state.set(newState);
                this.handleTimer(newState);
            }),
            switchMap((newState) => {
                // Backoff dinámico:
                // Si está procesando, preguntamos cada 2s.
                // Si está inactivo, preguntamos cada 5s para ahorrar recursos.
                const intervalMs = newState.is_processing ? 2000 : 5000;
                return timer(intervalMs).pipe(switchMap(() => this.startPollingRecursive()));
            })
        );
    }

    private handleTimer(status: AnalysisQueueState) {
        if (status.is_processing && status.current_job) {
            // Calcular tiempo real basado en servidor
            const startTime = status.current_job.start_time; // Unix timestamp in seconds

            if (!this.timerSubscription) {
                this.timerSubscription = interval(1000).subscribe(() => {
                    const now = Date.now() / 1000;
                    const diff = Math.max(0, Math.floor(now - startTime));
                    this._elapsedTime.set(diff);
                });
            }
        } else {
            // Detener cronómetro si no hay proceso
            if (this.timerSubscription) {
                this.timerSubscription.unsubscribe();
                this.timerSubscription = undefined;
            }
            this._elapsedTime.set(0);
        }
    }

    stopPolling(): void {
        this.pollingSubscription?.unsubscribe();
        this.pollingSubscription = undefined;
        if (this.timerSubscription) {
            this.timerSubscription.unsubscribe();
            this.timerSubscription = undefined;
        }
        this.lastKnownTimestamp = ''; // Resetear para que al volver a entrar refresque
    }
}